/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectHotelChangeState,
  selectIsSavedCommandFilterLoaded,
  selectShareUrlTabList,
  selectTabChangeState,
} from 'modules/dashboard/selectors';
import { dashboardActions } from 'modules/dashboard/slice';
import { UiController } from '..';
import { EXECUTION_TYPE, consoleCommands } from '../utils/constants';
import {
  executeMultipleCommands,
  getCommandKeys,
  resolveTabFromHash,
  setHotelCommand,
  splitCommandUrl,
} from '../utils/functions';

/**
 * Stages reported through onStage while a command url is applied
 */
export const COMMAND_URL_STAGE = {
  TAB: 'TAB',
  HOTEL: 'HOTEL',
  HOTEL_GROUP: 'HOTEL_GROUP',
  FILTERS: 'FILTERS',
};
/**
 * Start a new command run and execute the commands of a command url.
 * Call this once the tab and hotel commands of the url have been applied.
 * @param {Function} dispatch
 * @param {*} commandData - element that contains command url, it changes depends where it get executed
 * @param {*} type - type is defined as where execution function get called ex: chat, command, url
 */
export const runCommandSet = (dispatch, commandData, type) => {
  dispatch(dashboardActions.setCommandRun(getCommandKeys(commandData, type)));
  executeMultipleCommands(commandData, type);
};
/**
 * Custom hook to apply a command url: set the tab, set the hotel, then execute the remaining
 * commands once the tab and hotel changes are completed
 * @param {Boolean} ready - additional condition that should be true before commands are executed
 * @param {Number} delay - milliseconds to wait before commands are executed
 * @param {Function} onStage - called with a COMMAND_URL_STAGE value when a stage starts
 * @param {Function} onDone - called after the commands are executed
 * @returns {{applyUrl: Function, applyTab: Function, applySearch: Function}}
 */
const useApplyCommandUrl = ({ ready = true, delay = 0, onStage, onDone } = {}) => {
  const dispatch = useDispatch();
  //
  const tabChange = useSelector(selectTabChangeState);
  const hotelChange = useSelector(selectHotelChangeState);
  const isSavedCommandFilterLoaded = useSelector(selectIsSavedCommandFilterLoaded);
  const tabs = useSelector(selectShareUrlTabList);
  const [commandSet, setCommandSet] = useState('');
  // keep latest options without re-creating the handlers or re-triggering the execution effect
  const optionsRef = useRef(null);
  optionsRef.current = { ready, delay, onStage, onDone };

  // Set the tab from the hash part of a command url
  const applyTab = useCallback(
    (hash) => {
      optionsRef.current.onStage?.(COMMAND_URL_STAGE.TAB);
      dispatch(dashboardActions.setIsSavedCommandFilterLoaded(true));
      const tab = resolveTabFromHash(hash);
      if (tab !== undefined) {
        UiController.setTab(tab);
        dispatch(dashboardActions.setUrlTab(tabs[tab]));
      }
    },
    [dispatch, tabs]
  );
  // Set the hotel from the search part of a command url and keep the commands to be executed
  const applySearch = useCallback((search) => {
    if (!search) return;
    setCommandSet(search);
    const emitted = setHotelCommand(search, EXECUTION_TYPE.CONVERSATION);
    if (emitted) {
      optionsRef.current.onStage?.(
        emitted === consoleCommands.HOTEL ? COMMAND_URL_STAGE.HOTEL : COMMAND_URL_STAGE.HOTEL_GROUP
      );
    }
  }, []);
  // Apply a complete command url, ex: sd=lw&htl=Nomo Soho#pace
  const applyUrl = useCallback(
    (url) => {
      dispatch(dashboardActions.setIsSavedCommandFilterLoaded(true));
      const { search, hash } = splitCommandUrl(url);
      if (hash) {
        applyTab(hash);
      }
      applySearch(search);
    },
    [dispatch, applyTab, applySearch]
  );
  // Execute the remaining commands after tab and hotel commands are executed
  useEffect(() => {
    if (
      tabChange &&
      hotelChange &&
      commandSet &&
      isSavedCommandFilterLoaded &&
      optionsRef.current.ready
    ) {
      optionsRef.current.onStage?.(COMMAND_URL_STAGE.FILTERS);
      const execute = () => {
        runCommandSet(dispatch, commandSet, EXECUTION_TYPE.CONVERSATION);
        setCommandSet('');
        dispatch(dashboardActions.setTabChange(false));
        dispatch(dashboardActions.setHotelChange(false));
        dispatch(dashboardActions.setIsSavedCommandFilterLoaded(false));
        optionsRef.current.onDone?.();
      };
      if (optionsRef.current.delay > 0) {
        setTimeout(execute, optionsRef.current.delay);
      } else {
        execute();
      }
    }
  }, [tabChange, hotelChange, commandSet, isSavedCommandFilterLoaded, dispatch]);
  //
  return useMemo(() => ({ applyUrl, applyTab, applySearch }), [applyUrl, applyTab, applySearch]);
};

export default useApplyCommandUrl;
